import { useDatepicker } from '@datepicker-react/hooks';
import { DndContext } from '@dnd-kit/core';
import { isBefore } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useState, type FC, type ReactNode } from 'react';
import styled from 'styled-components';
import { useCallback } from 'use-memo-one';
import { Box } from 'src/components/box';
import { DropdownContent } from 'src/components/buttons-and-actions/dropdown-trigger';
import { VerticalDivider } from 'src/components/dividers';
import type {
  DayDraggableData,
  DayDroppableData,
  IsDayDragging,
} from 'src/components/form/datepicker/day';
import { GlobalDraggingStyle } from 'src/components/form/datepicker/global-dragging-style';
import { Month } from 'src/components/form/datepicker/month';
import {
  useTimeValue,
  TimeSelector,
} from 'src/components/form/datepicker/time-selector';
import { useCustomSensors } from 'src/components/form/datepicker/use-custom-sensors';
import type { InputTime } from 'src/components/form/datepicker/utils';
import {
  attachTime,
  checkBlockedDate,
} from 'src/components/form/datepicker/utils';
import { Stack } from 'src/components/layout/stack';
import type { DropdownState } from 'src/hooks/use-dropdown';
import { useNow } from 'src/hooks/use-now';
import { timeZone } from 'src/utils/date-time-format';

type DateRangePopoverProps = {
  setFloating: DropdownState['refs']['setFloating'];
  floatingStyle: DropdownState['style'];
  time: InputTime | [InputTime, InputTime];
  handleSelect: (value: { from: Date | null; to: Date | null }) => void;
  value: { from: Date | null; to: Date | null };
  minBookingDate?: Date;
  maxBookingDate?: Date;
  fromDisabled: boolean;
  toDisabled: boolean;
  fromHint: ReactNode;
  toHint: ReactNode;
  fromError: ReactNode;
  toError: ReactNode;
  isDateBlockedCustom?: (date: Date) => boolean;
};

export const DateRangePopover: FC<DateRangePopoverProps> = ({
  setFloating,
  floatingStyle,
  time,
  value,
  handleSelect,
  minBookingDate,
  maxBookingDate,
  fromDisabled,
  toDisabled,
  fromHint,
  toHint,
  fromError,
  toError,
  isDateBlockedCustom = () => false,
}) => {
  const fromTime = Array.isArray(time) ? time[0] : time;
  const toTime = Array.isArray(time) ? time[1] : time;

  // a hovered date or dragged-over date
  const [targetDate, setTargetDate] = useState<Date | null>(null);
  const now = new Date(useNow());

  const fromTimeValue = useTimeValue(value.from);

  const toTimeValue = useTimeValue(value.to);

  const {
    activeMonths,
    goToDate,
    goToPreviousMonthsByOneMonth,
    goToNextMonthsByOneMonth,
    isDateBlocked,
  } = useDatepicker({
    numberOfMonths: 2,
    startDate: value.from ?? now,
    endDate: value.to,
    focusedInput: null,
    onDatesChange: () => {},
    isDateBlocked(midnightInBerlin) {
      // date was already normalized before its passed to `useDay`,
      // so we know it's midnightInBerlin (but as UTC, so either 22:00 or 23:00).
      return (
        checkBlockedDate({
          midnightInBerlin,
          time: fromTime,
          minBookingDate,
          maxBookingDate,
        }) || isDateBlockedCustom(toZonedTime(midnightInBerlin, timeZone))
      );
    },
    // note: to avoid timezone specific errors, we need to handle minBookingDate/maxBookingDate
    // in our own dateBlockChecker
    // minBookingDate,
    // maxBookingDate,
  });

  const handleTimeSelect = useCallback(
    (from: Date | null, to: Date | null) => {
      if (from && to && isBefore(to, from) && !fromDisabled) {
        handleSelect({
          from: to,
          to: from,
        });
      } else {
        handleSelect({ from, to });
      }
    },
    [handleSelect, fromDisabled]
  );

  const handleDateSelect = useCallback(
    (date: Date) => {
      const normalizedStart = fromTime
        ? attachTime({
            originalDate: date,
            hours: fromTimeValue.value.hours,
            minutes: fromTimeValue.value.minutes,
            time: fromTime,
          })
        : date;
      const normalizedEnd = toTime
        ? attachTime({
            originalDate: date,
            hours: toTimeValue.value.hours,
            minutes: toTimeValue.value.minutes,
            time: toTime,
          })
        : date;

      if (!value.from) {
        handleSelect({
          from: normalizedStart,
          to: null,
        });
      } else if (fromDisabled) {
        handleSelect({
          from: value.from,
          to: normalizedEnd,
        });
      } else if (value.to) {
        handleSelect({
          from: normalizedStart,
          to: null,
        });
      } else {
        // the only place where we might swap start and end
        // is when users select a range within the calendar overlay.
        // for this check we have to take the current start and attach the end time
        // in case the "fromTime" is different to "toTime".
        const possibleNewEnd = toTime
          ? attachTime({
              originalDate: value.from,
              hours: fromTimeValue.value.hours,
              minutes: fromTimeValue.value.minutes,
              time: toTime,
            })
          : value.from;
        if (isBefore(normalizedStart, possibleNewEnd)) {
          handleSelect({
            from: normalizedStart,
            to: possibleNewEnd,
          });
        } else {
          handleSelect({
            from: value.from,
            to: normalizedEnd,
          });
        }
      }
    },
    [
      fromDisabled,
      fromTime,
      fromTimeValue,
      toTime,
      toTimeValue,
      value.from,
      value.to,
      handleSelect,
    ]
  );

  const [isDayDragging, setIsDayDragging] = useState<IsDayDragging>(null);

  const sensors = useCustomSensors();

  return (
    <DndContext
      sensors={sensors}
      onDragStart={(event) => {
        const data = event.active.data.current as DayDraggableData;
        setIsDayDragging(data.isStartDate ? 'start' : 'end');
      }}
      onDragOver={(event) => {
        if (!event.over) {
          setTargetDate(null);
          return;
        }
        const data = event.over.data.current as DayDroppableData;
        setTargetDate(data.date);
      }}
      onDragEnd={(event) => {
        setIsDayDragging(null);

        if (!event.over) return;
        const data = event.over.data.current as DayDroppableData;

        const normalizedStart = fromTime
          ? attachTime({
              originalDate: data.date,
              hours: fromTimeValue.value.hours,
              minutes: fromTimeValue.value.minutes,
              time: fromTime,
            })
          : data.date;
        const normalizedEnd = toTime
          ? attachTime({
              originalDate: data.date,
              hours: toTimeValue.value.hours,
              minutes: toTimeValue.value.minutes,
              time: toTime,
            })
          : data.date;

        if (isDayDragging === 'start') {
          const isBeforeEnd = isBefore(normalizedStart, value.to!);
          if (isBeforeEnd) {
            handleSelect({ from: normalizedStart, to: value.to });
          } else {
            const newFrom = fromTime
              ? attachTime({
                  originalDate: value.to!,
                  hours: fromTimeValue.value.hours,
                  minutes: fromTimeValue.value.minutes,
                  time: fromTime,
                })
              : value.to;
            handleSelect({ from: newFrom, to: normalizedEnd });
          }
        } else if (isDayDragging === 'end') {
          const isBeforeStart = isBefore(data.date, value.from!);
          if (isBeforeStart) {
            const newTo = toTime
              ? attachTime({
                  originalDate: value.from!,
                  hours: toTimeValue.value.hours,
                  minutes: toTimeValue.value.minutes,
                  time: toTime,
                })
              : value.from;
            handleSelect({ from: normalizedStart, to: newTo });
          } else {
            handleSelect({ from: value.from, to: normalizedEnd });
          }
        }
      }}
    >
      <GlobalDraggingStyle isDragging={Boolean(isDayDragging)} />
      <DropdownContent
        ref={setFloating}
        style={{ ...floatingStyle, maxWidth: 'min-content' }}
      >
        <Box>
          <Stack flow="column" gap={2} alignItems="start">
            {activeMonths.map((month, index) => (
              <Month
                key={`${month.year}-${month.month}`}
                year={month.year}
                month={month.month}
                monthOffset={index}
                date={month.date}
                startDate={value.from}
                endDate={value.to}
                isDayDragging={isDayDragging}
                targetDate={targetDate}
                onDayHover={(date) => {
                  if (isDayDragging) return;
                  setTargetDate(date);
                }}
                hideNextMonthButton={index === 0}
                hidePrevMonthButton={index !== 0}
                onChange={(date) => goToDate(date)}
                onNextMonthClick={goToNextMonthsByOneMonth}
                onPrevMonthClick={goToPreviousMonthsByOneMonth}
                isDateBlocked={isDateBlocked}
                onDateSelect={handleDateSelect}
              />
            ))}

            {(fromTime || toTime) && <VerticalDivider />}

            {(fromTime || toTime) && (
              <Stack gap={1}>
                {fromTime && (
                  <TimeSelector
                    namePrefix="start"
                    timeValue={fromTimeValue.value.timeString}
                    onTimeChange={(timeString) => {
                      const [hours, minutes] = timeString.split(':');
                      if (value.from)
                        handleTimeSelect(
                          attachTime({
                            originalDate: value.from,
                            hours,
                            minutes,
                            time: fromTime,
                          }),
                          value.to
                        );
                      else
                        fromTimeValue.setValue({
                          timeString,
                          hours,
                          minutes,
                        });
                    }}
                    time={fromTime}
                    disabled={fromDisabled}
                  />
                )}

                {toTime && (
                  <TimeSelector
                    namePrefix="end"
                    timeValue={toTimeValue.value.timeString}
                    onTimeChange={(timeString) => {
                      const [hours, minutes] = timeString.split(':');
                      if (value.to)
                        handleTimeSelect(
                          value.from,
                          attachTime({
                            originalDate: value.to,
                            hours,
                            minutes,
                            time: toTime,
                          })
                        );
                      else
                        toTimeValue.setValue({
                          timeString,
                          hours,
                          minutes,
                        });
                    }}
                    time={toTime}
                    disabled={toDisabled}
                  />
                )}
              </Stack>
            )}
          </Stack>

          {fromHint}
          {toHint}

          {(fromError || toError) && (
            <ErrorWrapper>
              {fromError}
              {toError}
            </ErrorWrapper>
          )}
        </Box>
      </DropdownContent>
    </DndContext>
  );
};

const ErrorWrapper = styled.div`
  margin-top: 1rem;
`;
